import React, { useState, useEffect, useRef, useCallback, Component, type ErrorInfo, type ReactNode } from 'react';
import DOMPurify from 'dompurify';
import { Layout } from '@/components/layout';
import { fmtDateTime } from '@/lib/dateFormat';
import { useAuthStore } from '@/lib/auth-store';
import { useToast } from '@/hooks/use-toast';
import {
  Layers, Plus, Play, CheckCircle2, XCircle, Loader2, RefreshCw,
  SkipForward, X, AlertCircle, FolderOpen, Wand2, Search, History, ChevronDown,
  Link2, ArrowRight, Check, Pencil, Info, ChevronsDown, ArrowDownToLine,
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ShopifyTargetStoreSelector } from '@/components/shopify-target-store';

const BASE = import.meta.env.BASE_URL.replace(/\/$/, '');

// ── Error Boundary ─────────────────────────────────────────────────────────────

interface EBState { error: Error | null }
class CollectionsErrorBoundary extends Component<{ children: ReactNode }, EBState> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error: Error): EBState { return { error }; }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[CollectionsErrorBoundary]', error, info);
  }
  render() {
    const { error } = this.state;
    if (error) {
      return (
        <div className="p-8 space-y-4 font-mono text-sm">
          <p className="text-red-400 font-bold">Collections page crashed — error details:</p>
          <pre className="bg-red-950/50 border border-red-800 rounded-lg p-4 text-red-200 whitespace-pre-wrap break-all">
            {error.name}: {error.message}
            {'\n\n'}
            {error.stack}
          </pre>
          <button
            onClick={() => this.setState({ error: null })}
            className="px-4 py-2 rounded-lg bg-muted hover:bg-muted/50 text-foreground transition-colors"
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface ShopifyStore { id: string; nickname: string; storeUrl: string }

type CollectionType = 'custom' | 'smart';

interface SmartRule {
  id: string;
  column: string;
  relation: string;
  condition: string;
  conditionObjectId?: string;
  label?: string;
}

interface CollectionDef {
  id: string;
  name: string;
  type: CollectionType;
  rules: SmartRule[];
  disjunctive: boolean;
}

interface CollectionResult {
  name: string;
  status: 'created' | 'skipped' | 'error';
  error?: string;
}

interface CollectionRun {
  id: string;
  storeName: string;
  totalCount: number;
  createdCount: number;
  skippedCount: number;
  errorCount: number;
  results: CollectionResult[];
  createdAt: string;
}

interface MetafieldDef {
  id: string;
  namespace: string;
  key: string;
  name: string;
  type: string;
  useAsCollectionCondition: boolean;
}

interface MetaSelection {
  def: MetafieldDef;
  checked: boolean;
  prefix: string;
}

interface ValuePreview {
  defId: string;
  namespace: string;
  key: string;
  defName: string;
  prefix: string;
  value: string;
  count: number;
  include: boolean;
}

// ── AI Description Writer types ───────────────────────────────────────────────

interface AiDescriptionResult {
  collectionId: string;
  title: string;
  handle: string;
  currentDescription: string;
  generatedDescription: string;
  editedDescription: string;
  approved: boolean;
}

// ── Metafield Linker types ────────────────────────────────────────────────────

interface ShopifyCollection { id: string; title: string; handle: string }

interface ColMetafieldDef {
  id: string;
  namespace: string;
  key: string;
  name: string;
  description?: string;
  type: { name: string };
}

interface ProductMetafieldDef {
  id: string;
  namespace: string;
  key: string;
  name: string;
  type: { name: string };
}

interface AutoLinkProposal {
  value: string;
  matchedCollection: ShopifyCollection | null;
  candidates: ShopifyCollection[];
  override?: ShopifyCollection | null;
  excluded?: boolean;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const COLUMN_OPTIONS = [
  { value: 'type',                    label: 'Product Type' },
  { value: 'title',                   label: 'Product Title' },
  { value: 'vendor',                  label: 'Vendor' },
  { value: 'tag',                     label: 'Tag' },
  { value: 'variant_price',           label: 'Price' },
  { value: 'variant_compare_at_price',label: 'Compare at Price' },
  { value: 'variant_weight',          label: 'Weight' },
  { value: 'variant_inventory',       label: 'Inventory Stock' },
  { value: 'variant_title',           label: 'Variant Title' },
];

const RELATION_OPTIONS = [
  { value: 'equals',      label: 'is equal to' },
  { value: 'not_equals',  label: 'is not equal to' },
  { value: 'contains',    label: 'contains' },
  { value: 'not_contains',label: 'does not contain' },
  { value: 'starts_with', label: 'starts with' },
  { value: 'ends_with',   label: 'ends with' },
  { value: 'greater_than',label: 'is greater than' },
  { value: 'less_than',   label: 'is less than' },
];

// Whether a collection metafield type stores collection references
function isCollectionRefType(typeName: string) {
  return typeName === 'list.collection_reference' || typeName === 'collection_reference';
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function genId() { return Math.random().toString(36).slice(2, 10); }

function emptyRule(): SmartRule {
  return { id: genId(), column: 'type', relation: 'equals', condition: '' };
}

function emptyForm(): CollectionDef {
  return { id: genId(), name: '', type: 'custom', rules: [emptyRule()], disjunctive: false };
}

// ── Debounce hook ─────────────────────────────────────────────────────────────
function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ── CollectionSearch: searchable picker for a single Shopify collection ───────
function CollectionSearch({
  storeId,
  token,
  value,
  onChange,
  placeholder = 'Search collections…',
}: {
  storeId: string;
  token: string;
  value: ShopifyCollection | null;
  onChange: (c: ShopifyCollection | null) => void;
  placeholder?: string;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ShopifyCollection[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const debouncedQuery = useDebounce(query, 300);

  useEffect(() => {
    if (!storeId || !token) return;
    setLoading(true);
    fetch(`${BASE}/api/collection-metafields/collections?storeId=${storeId}&q=${encodeURIComponent(debouncedQuery)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => setResults(Array.isArray(d.collections) ? d.collections : []))
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  }, [debouncedQuery, storeId, token]);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  if (value) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-primary/10 border border-primary/30 rounded-lg">
        <Layers className="w-3.5 h-3.5 text-primary shrink-0" />
        <span className="text-sm text-foreground font-medium flex-1 truncate">{value.title}</span>
        <span className="text-xs text-muted-foreground shrink-0">{value.handle}</span>
        <button onClick={() => onChange(null)} className="text-muted-foreground hover:text-red-400 transition-colors shrink-0">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div ref={ref} className="relative">
      <div className="flex items-center gap-2 px-3 py-2 bg-muted/50 border border-border rounded-lg focus-within:border-primary/50 transition-colors">
        <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
        <input
          className="flex-1 bg-transparent text-sm text-foreground placeholder:text-slate-600 outline-none"
          placeholder={placeholder}
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
        />
        {loading && <Loader2 className="w-3.5 h-3.5 text-muted-foreground animate-spin shrink-0" />}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-50 top-full mt-1 left-0 right-0 bg-card border border-border rounded-lg shadow-xl max-h-60 overflow-y-auto">
          {results.map(c => (
            <button
              key={c.id}
              className="w-full text-left px-3 py-2.5 hover:bg-muted/50 transition-colors flex items-center gap-2"
              onClick={() => { onChange(c); setOpen(false); setQuery(''); }}
            >
              <Layers className="w-3 h-3 text-muted-foreground shrink-0" />
              <span className="text-sm text-foreground flex-1 truncate">{c.title}</span>
              <span className="text-xs text-slate-600 shrink-0">{c.handle}</span>
            </button>
          ))}
        </div>
      )}
      {open && !loading && results.length === 0 && query.length > 1 && (
        <div className="absolute z-50 top-full mt-1 left-0 right-0 bg-card border border-border rounded-lg shadow-xl px-3 py-2.5">
          <p className="text-xs text-muted-foreground">No collections found</p>
        </div>
      )}
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export function Collections() {
  return (
    <CollectionsErrorBoundary>
      <CollectionsInner />
    </CollectionsErrorBoundary>
  );
}

function CollectionsInner() {
  const { token } = useAuthStore();
  const { toast } = useToast();

  const [stores, setStores] = useState<ShopifyStore[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState('');

  // Tab state
  const [activeTab, setActiveTab] = useState<'create' | 'linker' | 'ai-writer' | 'oos-sort'>('create');

  // ── Create-tab state ──────────────────────────────────────────────────────
  const [form, setForm] = useState<CollectionDef>(emptyForm());
  const [staging, setStaging] = useState<CollectionDef[]>([]);
  const [creating, setCreating] = useState(false);
  const [results, setResults] = useState<CollectionResult[] | null>(null);

  const [runs, setRuns] = useState<CollectionRun[]>([]);
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);

  const [metaDefs, setMetaDefs] = useState<MetafieldDef[]>([]);
  const [metaDefsLoading, setMetaDefsLoading] = useState(false);
  const [metaSelections, setMetaSelections] = useState<MetaSelection[]>([]);
  const [valuePreviews, setValuePreviews] = useState<ValuePreview[]>([]);
  const [valuesLoading, setValuesLoading] = useState(false);

  // ── Metafield Linker state ────────────────────────────────────────────────
  const [parentCollection, setParentCollection] = useState<ShopifyCollection | null>(null);
  const [colMetaDefs, setColMetaDefs] = useState<ColMetafieldDef[]>([]);
  const [colMetaDefsLoading, setColMetaDefsLoading] = useState(false);
  const [selectedColMeta, setSelectedColMeta] = useState<ColMetafieldDef | null>(null);
  const [currentMetaValue, setCurrentMetaValue] = useState<string>('');

  // Manual mode
  const [manualRefs, setManualRefs] = useState<ShopifyCollection[]>([]);
  const [manualSearch, setManualSearch] = useState<ShopifyCollection | null>(null);

  // Auto-link mode
  const [linkerMode, setLinkerMode] = useState<'manual' | 'auto'>('manual');
  const [productMetaDefs, setProductMetaDefs] = useState<ProductMetafieldDef[]>([]);
  const [productMetaDefsLoading, setProductMetaDefsLoading] = useState(false);
  const [selectedProductMeta, setSelectedProductMeta] = useState<ProductMetafieldDef | null>(null);
  const [proposals, setProposals] = useState<AutoLinkProposal[]>([]);
  const [scanning, setScanning] = useState(false);
  const [scanPages, setScanPages] = useState(0);

  // Override picker state: which proposal is being overridden
  const [overridingIdx, setOverridingIdx] = useState<number | null>(null);

  // Apply state
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState<{ ok: boolean; message: string } | null>(null);

  // Manual text value (for non-reference metafields)
  const [manualTextValue, setManualTextValue] = useState('');

  // ── AI Description Writer state ───────────────────────────────────────────
  const [aiSelectMode, setAiSelectMode] = useState<'manual' | 'metafield'>('manual');
  const [aiSelectedCollections, setAiSelectedCollections] = useState<Array<{ id: string; title: string; handle: string; bodyHtml?: string }>>([]);
  const [aiSearchQuery, setAiSearchQuery] = useState('');
  const [aiSearchResults, setAiSearchResults] = useState<Array<{ id: string; title: string; handle: string }>>([]);
  const [aiSearchLoading, setAiSearchLoading] = useState(false);
  const [aiSearchOpen, setAiSearchOpen] = useState(false);
  const [aiSearchPageInfo, setAiSearchPageInfo] = useState<{ hasNextPage: boolean; endCursor: string | null }>({ hasNextPage: false, endCursor: null });
  const [aiSearchError, setAiSearchError] = useState<string | null>(null);
  const [aiMetaNs, setAiMetaNs] = useState('');
  const [aiMetaKey, setAiMetaKey] = useState('');
  const [aiMetaLoading, setAiMetaLoading] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiPhase, setAiPhase] = useState<'idle' | 'fetching' | 'generating'>('idle');
  const [aiProgress, setAiProgress] = useState({ current: 0, total: 0 });
  const [aiResults, setAiResults] = useState<AiDescriptionResult[]>([]);
  const [aiApplying, setAiApplying] = useState(false);
  const [aiApplyResult, setAiApplyResult] = useState<{ ok: boolean; message: string } | null>(null);
  const aiSearchRef = useRef<HTMLDivElement>(null);
  const aiSearchRequestRef = useRef(0);
  const aiSearchAbortRef = useRef<AbortController | null>(null);
  const aiSearchContextRef = useRef('');

  // ── OOS Sort state ────────────────────────────────────────────────────────
  interface OosSortResult {
    collectionId: string;
    title: string;
    skipped: boolean;
    reason?: string;
    error?: string;
    movedCount: number;
    totalCount: number;
    switchedToManual?: boolean;
    message?: string;
  }
  const [oosMode, setOosMode] = useState<'manual' | 'all'>('manual');
  const [oosSelectedCollections, setOosSelectedCollections] = useState<Array<{ id: string; title: string; handle: string }>>([]);
  const [oosSearchQuery, setOosSearchQuery] = useState('');
  const [oosSearchResults, setOosSearchResults] = useState<Array<{ id: string; title: string; handle: string }>>([]);
  const [oosSearchLoading, setOosSearchLoading] = useState(false);
  const [oosSearchOpen, setOosSearchOpen] = useState(false);
  const [oosRunning, setOosRunning] = useState(false);
  const [oosProgress, setOosProgress] = useState({ current: 0, total: 0 });
  const [oosResults, setOosResults] = useState<OosSortResult[]>([]);
  const [oosStatusMsg, setOosStatusMsg] = useState('');
  const oosSearchRef = useRef<HTMLDivElement>(null);

  // ── Load stores ───────────────────────────────────────────────────────────
  const fetchRuns = useCallback(() => {
    if (!token) return;
    fetch(`${BASE}/api/stores/collection-runs`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => setRuns(Array.isArray(data.runs) ? data.runs : []))
      .catch(() => {});
  }, [token]);

  useEffect(() => {
    if (!token) return;
    fetch(`${BASE}/api/stores`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => {
        const list: ShopifyStore[] = Array.isArray(data) ? data : [];
        setStores(list);
        if (list.length === 1) setSelectedStoreId(list[0]!.id);
      })
      .catch(() => toast({ title: 'Failed to load stores', variant: 'destructive' }));
    fetchRuns();
  }, [token, fetchRuns]);

  // ── Load product metafield defs (Create tab) ──────────────────────────────
  useEffect(() => {
    setMetaDefs([]);
    setMetaSelections([]);
    setValuePreviews([]);
    if (!selectedStoreId || !token) return;

    setMetaDefsLoading(true);
    fetch(`${BASE}/api/stores/${selectedStoreId}/metafields/definitions`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async r => {
        const data = await r.json();
        if (!r.ok) throw new Error(data.error ?? 'Failed to load metafield definitions');
        const defs: MetafieldDef[] = Array.isArray(data) ? data : [];
        setMetaDefs(defs);
        setMetaSelections(defs.map(d => ({ def: d, checked: false, prefix: '' })));
      })
      .catch((err: any) => toast({ title: err.message ?? 'Failed to load metafield definitions', variant: 'destructive' }))
      .finally(() => setMetaDefsLoading(false));
  }, [selectedStoreId, token]);

  // ── Load collection metafield defs (Linker tab) ───────────────────────────
  useEffect(() => {
    setColMetaDefs([]);
    setSelectedColMeta(null);
    if (!selectedStoreId || !token) return;

    setColMetaDefsLoading(true);
    fetch(`${BASE}/api/collection-metafields/definitions?storeId=${selectedStoreId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async r => {
        const data = await r.json();
        if (!r.ok) throw new Error(data.error ?? 'Failed');
        setColMetaDefs(Array.isArray(data) ? data : []);
      })
      .catch(() => {})
      .finally(() => setColMetaDefsLoading(false));
  }, [selectedStoreId, token]);

  // ── Load product metafield defs for auto-link ─────────────────────────────
  useEffect(() => {
    setProductMetaDefs([]);
    setSelectedProductMeta(null);
    if (!selectedStoreId || !token || activeTab !== 'linker') return;

    setProductMetaDefsLoading(true);
    fetch(`${BASE}/api/collection-metafields/product-metafield-defs?storeId=${selectedStoreId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async r => {
        const data = await r.json();
        if (!r.ok) throw new Error(data.error ?? 'Failed');
        setProductMetaDefs(Array.isArray(data) ? data : []);
      })
      .catch(() => {})
      .finally(() => setProductMetaDefsLoading(false));
  }, [selectedStoreId, token, activeTab]);

  // ── Load current value when parent collection + metafield selected ─────────
  useEffect(() => {
    setCurrentMetaValue('');
    setManualRefs([]);
    setProposals([]);
    setApplyResult(null);
    if (!selectedStoreId || !parentCollection || !selectedColMeta || !token) return;

    fetch(
      `${BASE}/api/collection-metafields/values?storeId=${selectedStoreId}&collectionId=${encodeURIComponent(parentCollection.id)}`,
      { headers: { Authorization: `Bearer ${token}` } },
    )
      .then(async r => {
        const data = await r.json();
        if (!r.ok) return;
        const mf = (data.metafields ?? []).find(
          (m: any) => m.namespace === selectedColMeta.namespace && m.key === selectedColMeta.key,
        );
        if (mf) {
          setCurrentMetaValue(mf.value);
          // Pre-populate manual refs for collection_reference types
          if (isCollectionRefType(selectedColMeta.type.name)) {
            try {
              const ids: string[] = JSON.parse(mf.value);
              // We only have IDs here — show them as-is (titles unknown without extra fetches)
              setManualRefs(ids.map(id => ({ id, title: id.split('/').pop() ?? id, handle: '' })));
            } catch { /* single ref */ }
          } else {
            setManualTextValue(mf.value);
          }
        }
      })
      .catch(() => {});
  }, [selectedStoreId, parentCollection, selectedColMeta, token]);

  // ── Create-tab helpers ────────────────────────────────────────────────────
  const addRule = () => setForm(f => ({ ...f, rules: [...f.rules, emptyRule()] }));
  const removeRule = (ruleId: string) => setForm(f => ({ ...f, rules: f.rules.filter(r => r.id !== ruleId) }));
  const updateRule = (ruleId: string, patch: Partial<SmartRule>) =>
    setForm(f => ({ ...f, rules: f.rules.map(r => r.id === ruleId ? { ...r, ...patch } : r) }));

  const addToStaging = () => {
    const name = form.name.trim();
    if (!name) { toast({ title: 'Collection name is required', variant: 'destructive' }); return; }
    if (form.type === 'smart' && form.rules.some(r => !r.condition.trim())) {
      toast({ title: 'Fill in all rule conditions', variant: 'destructive' });
      return;
    }
    setStaging(prev => [...prev, { ...form, rules: form.rules.map(r => ({ ...r })), id: genId() }]);
    setForm(emptyForm());
  };

  const toggleMetaSelection = (index: number) =>
    setMetaSelections(prev => prev.map((s, i) => i === index ? { ...s, checked: !s.checked } : s));
  const updateMetaPrefix = (index: number, prefix: string) =>
    setMetaSelections(prev => prev.map((s, i) => i === index ? { ...s, prefix } : s));

  const loadValuePreviews = async () => {
    const checked = metaSelections.filter(s => s.checked);
    if (checked.length === 0) return;
    setValuesLoading(true);
    setValuePreviews([]);
    try {
      const allPreviews: ValuePreview[] = [];
      for (const sel of checked) {
        const params = new URLSearchParams({ namespace: sel.def.namespace, key: sel.def.key });
        const r = await fetch(`${BASE}/api/stores/${selectedStoreId}/metafields/values?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await r.json();
        if (!r.ok) throw new Error(data.error ?? 'Failed to load values');
        const values: { value: string; count: number }[] = Array.isArray(data) ? data : [];
        for (const v of values) {
          allPreviews.push({
            defId: sel.def.id, namespace: sel.def.namespace, key: sel.def.key,
            defName: sel.def.name, prefix: sel.prefix, value: v.value, count: v.count, include: true,
          });
        }
      }
      setValuePreviews(allPreviews);
      if (allPreviews.length === 0)
        toast({ title: 'No metafield values found across products', variant: 'destructive' });
    } catch (err: any) {
      toast({ title: err.message ?? 'Failed to load values', variant: 'destructive' });
    } finally { setValuesLoading(false); }
  };

  const toggleValueInclude = (defId: string, value: string) =>
    setValuePreviews(prev => prev.map(p => p.defId === defId && p.value === value ? { ...p, include: !p.include } : p));
  const setGroupInclude = (defId: string, include: boolean) =>
    setValuePreviews(prev => prev.map(p => p.defId === defId ? { ...p, include } : p));

  const addPreviewsToStaging = () => {
    const included = valuePreviews.filter(v => v.include);
    if (included.length === 0) return;
    const newCollections: CollectionDef[] = included.map(v => ({
      id: genId(), name: `${v.prefix}${v.value}`, type: 'smart' as const,
      rules: [{
        id: genId(), column: 'product_metafield_definition', relation: 'equals',
        condition: v.value, conditionObjectId: v.defId, label: `${v.namespace}.${v.key} = "${v.value}"`,
      }],
      disjunctive: false,
    }));
    setStaging(prev => [...prev, ...newCollections]);
    setValuePreviews([]);
    toast({ title: `${newCollections.length} collections added to staging` });
  };

  const createAll = async (collectionsOverride?: CollectionDef[]) => {
    if (!selectedStoreId) { toast({ title: 'Select a store first', variant: 'destructive' }); return; }
    const toCreate = collectionsOverride ?? staging;
    if (toCreate.length === 0) { toast({ title: 'Add at least one collection', variant: 'destructive' }); return; }
    setCreating(true);
    setResults(null);
    try {
      const r = await fetch(`${BASE}/api/stores/${selectedStoreId}/collections/bulk`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          collections: toCreate.map(c => ({
            name: c.name, type: c.type,
            rules: c.rules.map(({ column, relation, condition, conditionObjectId }) => ({
              column, relation, condition, ...(conditionObjectId ? { conditionObjectId } : {}),
            })),
            disjunctive: c.disjunctive,
          })),
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error ?? 'Failed to create collections');
      const res: CollectionResult[] = Array.isArray(data.results) ? data.results : [];
      setResults(res);
      fetchRuns();
      const created = res.filter(x => x.status === 'created').length;
      const skipped = res.filter(x => x.status === 'skipped').length;
      const errors  = res.filter(x => x.status === 'error').length;
      toast({ title: `Done — ${created} created, ${skipped} skipped, ${errors} failed`, variant: errors > 0 ? 'destructive' : 'default' });
      if (errors === 0) setStaging([]);
    } catch (err: any) {
      toast({ title: err.message ?? 'Failed to create collections', variant: 'destructive' });
    } finally { setCreating(false); }
  };

  const retryFailed = () => {
    if (!results) return;
    const failedNames = new Set(results.filter(r => r.status === 'error').map(r => r.name));
    createAll(staging.filter(c => failedNames.has(c.name)));
  };

  // ── Metafield Linker helpers ───────────────────────────────────────────────

  const addManualRef = (col: ShopifyCollection) => {
    if (!manualRefs.find(r => r.id === col.id)) {
      setManualRefs(prev => [...prev, col]);
    }
    setManualSearch(null);
  };

  const removeManualRef = (id: string) => setManualRefs(prev => prev.filter(r => r.id !== id));

  const runAutoLink = async () => {
    if (!selectedStoreId || !parentCollection || !selectedProductMeta) return;
    setScanning(true);
    setProposals([]);
    setApplyResult(null);
    try {
      const r = await fetch(`${BASE}/api/collection-metafields/auto-link`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          storeId: selectedStoreId,
          collectionId: parentCollection.id,
          productMetaNs: selectedProductMeta.namespace,
          productMetaKey: selectedProductMeta.key,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error ?? 'Scan failed');
      setScanPages(data.scannedPages ?? 0);
      const props: AutoLinkProposal[] = (data.proposals ?? []).map((p: any) => ({ ...p, excluded: false }));
      setProposals(props);
      if (props.length === 0) {
        toast({ title: data.message ?? 'No values found', variant: 'destructive' });
      }
    } catch (err: any) {
      toast({ title: err.message ?? 'Scan failed', variant: 'destructive' });
    } finally { setScanning(false); }
  };

  const applyLinker = async () => {
    if (!selectedStoreId || !parentCollection || !selectedColMeta) return;

    let value: string;

    if (isCollectionRefType(selectedColMeta.type.name)) {
      const refs =
        linkerMode === 'manual'
          ? manualRefs
          : proposals
              .filter(p => !p.excluded)
              .map(p => p.override !== undefined ? p.override : p.matchedCollection)
              .filter(Boolean) as ShopifyCollection[];

      if (refs.length === 0) {
        toast({ title: 'No collections selected', variant: 'destructive' });
        return;
      }

      if (selectedColMeta.type.name === 'list.collection_reference') {
        value = JSON.stringify(refs.map(r => r.id));
      } else {
        value = refs[0]!.id;
      }
    } else {
      value = manualTextValue.trim();
      if (!value) { toast({ title: 'Value cannot be empty', variant: 'destructive' }); return; }
    }

    setApplying(true);
    setApplyResult(null);
    try {
      const r = await fetch(`${BASE}/api/collection-metafields/set`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          storeId: selectedStoreId,
          collectionId: parentCollection.id,
          namespace: selectedColMeta.namespace,
          key: selectedColMeta.key,
          type: selectedColMeta.type.name,
          value,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error ?? 'Failed to apply');
      setApplyResult({ ok: true, message: 'Metafield updated successfully' });
      setCurrentMetaValue(value);
      toast({ title: `✓ ${selectedColMeta.name} updated on "${parentCollection.title}"` });
    } catch (err: any) {
      setApplyResult({ ok: false, message: err.message ?? 'Failed to apply' });
      toast({ title: err.message ?? 'Failed to apply', variant: 'destructive' });
    } finally { setApplying(false); }
  };

  // ── AI Description Writer helpers ─────────────────────────────────────────

  const debouncedAiSearch = useDebounce(aiSearchQuery, 300);
  const aiSearchContext = `${selectedStoreId}\u0000${aiSelectMode}\u0000${activeTab}\u0000${aiSearchQuery}`;

  // Do not wait for the debounce before invalidating an in-flight request. This
  // prevents a response for the prior text/store/mode from populating the picker.
  useEffect(() => {
    aiSearchContextRef.current = aiSearchContext;
    aiSearchRequestRef.current++;
    aiSearchAbortRef.current?.abort();
    aiSearchAbortRef.current = null;
    setAiSearchLoading(false);
  }, [aiSearchContext]);

  const fetchAiCollections = useCallback(async (cursor?: string | null, append = false) => {
    if (!selectedStoreId || !token || aiSelectMode !== 'manual' || activeTab !== 'ai-writer') return;
    const requestId = ++aiSearchRequestRef.current;
    const requestContext = aiSearchContext;
    aiSearchAbortRef.current?.abort();
    const controller = new AbortController();
    aiSearchAbortRef.current = controller;
    setAiSearchLoading(true);
    setAiSearchError(null);
    try {
      const params = new URLSearchParams({ storeId: selectedStoreId, q: debouncedAiSearch });
      if (cursor) params.set('cursor', cursor);
      const r = await fetch(`${BASE}/api/collection-descriptions/collections?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error ?? 'Failed to load collections');
      if (requestId !== aiSearchRequestRef.current || requestContext !== aiSearchContextRef.current) return;
      const collections = Array.isArray(d.collections) ? d.collections : [];
      setAiSearchResults(previous => append ? [...previous, ...collections] : collections);
      setAiSearchPageInfo({
        hasNextPage: d.pageInfo?.hasNextPage === true,
        endCursor: d.pageInfo?.endCursor ?? null,
      });
    } catch (err: any) {
      if (err.name === 'AbortError' || requestId !== aiSearchRequestRef.current || requestContext !== aiSearchContextRef.current) return;
      const message = err.message ?? 'Failed to load collections';
      setAiSearchError(message);
      if (!append) {
        setAiSearchResults([]);
        setAiSearchPageInfo({ hasNextPage: false, endCursor: null });
      }
      toast({ title: message, variant: 'destructive' });
    } finally {
      if (requestId === aiSearchRequestRef.current && requestContext === aiSearchContextRef.current) {
        setAiSearchLoading(false);
      }
    }
  }, [activeTab, aiSearchContext, aiSelectMode, debouncedAiSearch, selectedStoreId, toast, token]);

  useEffect(() => {
    void fetchAiCollections();
  }, [fetchAiCollections]);

  useEffect(() => {
    function h(e: MouseEvent) { if (aiSearchRef.current && !aiSearchRef.current.contains(e.target as Node)) setAiSearchOpen(false); }
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const addAiCollection = (col: { id: string; title: string; handle: string; bodyHtml?: string }) => {
    if (!aiSelectedCollections.find(c => c.id === col.id)) {
      setAiSelectedCollections(prev => [...prev, col]);
    }
    setAiSearchQuery(''); setAiSearchOpen(false); setAiSearchResults([]);
  };

  const removeAiCollection = (id: string) => setAiSelectedCollections(prev => prev.filter(c => c.id !== id));

  const loadByMetafield = async () => {
    if (!selectedStoreId || !aiMetaNs.trim() || !aiMetaKey.trim()) {
      toast({ title: 'Enter namespace and key', variant: 'destructive' }); return;
    }
    setAiMetaLoading(true);
    try {
      const r = await fetch(`${BASE}/api/collection-descriptions/by-metafield`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ storeId: selectedStoreId, namespace: aiMetaNs.trim(), key: aiMetaKey.trim() }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error ?? 'Failed');
      const cols: Array<{ id: string; title: string; handle: string; bodyHtml?: string }> = d.collections ?? [];
      if (cols.length === 0) { toast({ title: 'No collections found with that metafield', variant: 'destructive' }); return; }
      setAiSelectedCollections(cols);
      toast({ title: `${cols.length} collections loaded` });
    } catch (err: any) {
      toast({ title: err.message ?? 'Failed to load', variant: 'destructive' });
    } finally { setAiMetaLoading(false); }
  };

  const generateDescriptions = async () => {
    if (!selectedStoreId || aiSelectedCollections.length === 0) return;
    setAiGenerating(true);
    setAiResults([]);
    setAiApplyResult(null);
    setAiPhase('fetching');
    setAiProgress({ current: 0, total: aiSelectedCollections.length });

    try {
      const resp = await fetch(`${BASE}/api/collection-descriptions/generate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ storeId: selectedStoreId, collectionIds: aiSelectedCollections.map(c => c.id) }),
      });
      if (!resp.ok || !resp.body) throw new Error('Stream failed to start');

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split('\n\n');
        buf = parts.pop() ?? '';
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith('data:')) continue;
          try {
            const ev = JSON.parse(line.slice(5).trim());
            if (ev.type === 'progress') {
              setAiPhase(ev.phase === 'generating' ? 'generating' : 'fetching');
              setAiProgress({ current: ev.current, total: ev.total });
            } else if (ev.type === 'result') {
              setAiResults(prev => [...prev, {
                collectionId: ev.collectionId,
                title: ev.title,
                handle: ev.handle,
                currentDescription: ev.currentDescription ?? '',
                generatedDescription: ev.generatedDescription,
                editedDescription: ev.generatedDescription,
                approved: true,
              }]);
              setAiProgress({ current: ev.current, total: ev.total });
            } else if (ev.type === 'done') {
              setAiPhase('idle');
            } else if (ev.type === 'error') {
              toast({ title: ev.message ?? 'Generation failed', variant: 'destructive' });
            }
          } catch { /* skip */ }
        }
      }
    } catch (err: any) {
      toast({ title: err.message ?? 'Generation failed', variant: 'destructive' });
    } finally {
      setAiGenerating(false);
      setAiPhase('idle');
    }
  };

  const applyDescriptions = async () => {
    const approved = aiResults.filter(r => r.approved);
    if (!selectedStoreId || approved.length === 0) {
      toast({ title: 'No approved descriptions to apply', variant: 'destructive' }); return;
    }
    setAiApplying(true);
    setAiApplyResult(null);
    try {
      const r = await fetch(`${BASE}/api/collection-descriptions/apply`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          storeId: selectedStoreId,
          updates: approved.map(r => ({ collectionId: r.collectionId, description: r.editedDescription })),
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error ?? 'Failed');
      setAiApplyResult({
        ok: true,
        message: `${d.successCount} description${d.successCount !== 1 ? 's' : ''} applied to Shopify${d.errorCount > 0 ? ` (${d.errorCount} failed)` : ''}`,
      });
      toast({ title: `✓ ${d.successCount} collection descriptions updated` });
    } catch (err: any) {
      setAiApplyResult({ ok: false, message: err.message ?? 'Apply failed' });
      toast({ title: err.message ?? 'Apply failed', variant: 'destructive' });
    } finally { setAiApplying(false); }
  };

  // ── OOS sort search ───────────────────────────────────────────────────────
  const debouncedOosQuery = useDebounce(oosSearchQuery, 300);
  useEffect(() => {
    if (!selectedStoreId || !token || activeTab !== 'oos-sort') return;
    setOosSearchLoading(true);
    fetch(`${BASE}/api/collection-descriptions/collections?storeId=${selectedStoreId}&q=${encodeURIComponent(debouncedOosQuery)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => setOosSearchResults(Array.isArray(d.collections) ? d.collections : []))
      .catch(() => setOosSearchResults([]))
      .finally(() => setOosSearchLoading(false));
  }, [debouncedOosQuery, selectedStoreId, token, activeTab]);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (oosSearchRef.current && !oosSearchRef.current.contains(e.target as Node)) setOosSearchOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const addOosCollection = (c: { id: string; title: string; handle: string }) => {
    if (!oosSelectedCollections.some(x => x.id === c.id)) {
      setOosSelectedCollections(prev => [...prev, c]);
    }
    setOosSearchQuery('');
    setOosSearchOpen(false);
  };

  const runOosSort = async () => {
    if (!selectedStoreId) return;
    if (oosMode === 'manual' && oosSelectedCollections.length === 0) {
      toast({ title: 'Select at least one collection', variant: 'destructive' }); return;
    }
    setOosRunning(true);
    setOosResults([]);
    setOosProgress({ current: 0, total: 0 });
    setOosStatusMsg('');

    try {
      const body = {
        storeId: selectedStoreId,
        collectionIds: oosMode === 'all' ? 'all' : oosSelectedCollections.map(c => c.id),
      };
      const resp = await fetch(`${BASE}/api/collection-sort/push-oos-to-end`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!resp.ok || !resp.body) throw new Error('Stream failed to start');

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split('\n\n');
        buf = parts.pop() ?? '';
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith('data:')) continue;
          try {
            const ev = JSON.parse(line.slice(5).trim());
            if (ev.type === 'status') {
              setOosStatusMsg(ev.message ?? '');
            } else if (ev.type === 'progress') {
              setOosProgress({ current: ev.current, total: ev.total });
            } else if (ev.type === 'result') {
              setOosResults(prev => [...prev, ev as OosSortResult]);
            } else if (ev.type === 'error') {
              toast({ title: ev.message ?? 'Sort failed', variant: 'destructive' });
            }
          } catch { /* skip malformed */ }
        }
      }
    } catch (err: any) {
      toast({ title: err.message ?? 'Sort failed', variant: 'destructive' });
    } finally {
      setOosRunning(false);
      setOosStatusMsg('');
    }
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  const checkedSelections = metaSelections.filter(s => s.checked);
  const includedCount = valuePreviews.filter(v => v.include).length;
  const groupedPreviews = checkedSelections
    .map(s => ({ ...s, values: valuePreviews.filter(v => v.defId === s.def.id) }))
    .filter(g => g.values.length > 0);

  const confirmedProposals = proposals.filter(p => !p.excluded && (p.override !== undefined ? p.override : p.matchedCollection) !== null);
  const unmatchedProposals = proposals.filter(p => !p.excluded && (p.override !== undefined ? p.override : p.matchedCollection) === null);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground">Collections</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Create Shopify collections in bulk, or link collection metafields to other collections.
          </p>
        </div>

        {/* Store picker */}
        <div className="glass-panel p-4 rounded-xl">
          <ShopifyTargetStoreSelector
            stores={stores}
            value={selectedStoreId}
            onChange={v => {
              setSelectedStoreId(v);
              setParentCollection(null);
              setSelectedColMeta(null);
              setProposals([]);
              setManualRefs([]);
              setApplyResult(null);
            }}
            className="max-w-sm"
          />
          {stores.length === 0 && (
            <p className="text-xs text-muted-foreground">
              No stores connected yet — add one in <strong>Connected Stores</strong>.
            </p>
          )}
        </div>

        {/* Tab switcher */}
        <div className="flex gap-1 p-1 bg-muted/50 border border-border/50 rounded-xl w-fit">
          {([
            { id: 'create',    label: 'Create Collections',    icon: Plus },
            { id: 'linker',    label: 'Metafield Linker',      icon: Link2 },
            { id: 'ai-writer', label: 'AI Description Writer', icon: Wand2 },
            { id: 'oos-sort',  label: 'OOS to End',            icon: ChevronsDown },
          ] as const).map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-primary/20 border border-primary/30 text-primary'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* ════════════════════════════════════════════════════════════════════ */}
        {/* TAB: Create Collections                                             */}
        {/* ════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'create' && (
          <>
            {/* Scope note */}
            <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-amber-500/5 border border-amber-500/20 text-xs text-amber-400/80">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                If creation fails with a scope error, your store's OAuth token doesn't include{' '}
                <code className="text-amber-300">write_collections</code>. Re-connect the store via{' '}
                <strong>Connected Stores → Add Store</strong> to grant the new permission.
              </span>
            </div>

            {/* Auto-generate from Metafields */}
            {selectedStoreId && (
              <div className="glass-panel rounded-xl p-6 space-y-5">
                <div>
                  <h2 className="font-semibold text-foreground flex items-center gap-2">
                    <Wand2 className="w-4 h-4 text-primary" />
                    Auto-generate from Metafields
                  </h2>
                  <p className="text-xs text-muted-foreground mt-1">
                    Select product metafields to scan for unique values — one smart collection is generated per value.
                  </p>
                </div>

                {metaDefsLoading ? (
                  <div className="flex items-center gap-2 text-muted-foreground text-sm">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading metafield definitions…
                  </div>
                ) : metaDefs.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No product metafield definitions found. Create them in Shopify under{' '}
                    <strong>Settings → Custom data → Products</strong>.
                  </p>
                ) : (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">Select metafields to scan</p>
                      {metaSelections.map((sel, i) => {
                        const supported = sel.def.useAsCollectionCondition;
                        return (
                          <div key={sel.def.id} className="flex flex-wrap items-center gap-2">
                            <button
                              onClick={() => supported && toggleMetaSelection(i)}
                              disabled={!supported}
                              title={supported ? undefined : `Shopify does not support "${sel.def.type}" as a smart collection condition`}
                              className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all ${
                                !supported
                                  ? 'bg-muted/30 border-border/50 text-slate-600 cursor-not-allowed opacity-60'
                                  : sel.checked
                                    ? 'bg-primary/15 border-primary/50 text-primary'
                                    : 'bg-muted/50 border-border text-muted-foreground hover:text-foreground hover:bg-muted'
                              }`}
                            >
                              <span className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
                                sel.checked && supported ? 'border-primary bg-primary/30' : 'border-slate-600'
                              }`}>
                                {sel.checked && supported && <span className="w-1.5 h-1.5 rounded-full bg-primary" />}
                              </span>
                              <span className="font-medium">{sel.def.name}</span>
                              <span className="text-xs opacity-50">{sel.def.namespace}.{sel.def.key}</span>
                              <span className="text-xs opacity-40 italic">{sel.def.type}</span>
                              {!supported && (
                                <span className="text-xs px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-500/30 text-amber-500 font-medium">
                                  not supported
                                </span>
                              )}
                            </button>
                            {sel.checked && supported && (
                              <input
                                className="h-9 w-52 bg-muted/50 border border-border rounded-lg px-3 text-sm text-foreground placeholder:text-slate-600 focus:outline-none focus:border-primary/50 transition-colors"
                                placeholder='Name prefix, e.g. "Make: "'
                                value={sel.prefix}
                                onChange={e => updateMetaPrefix(i, e.target.value)}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>

                    <button
                      onClick={loadValuePreviews}
                      disabled={checkedSelections.length === 0 || valuesLoading}
                      className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary/20 hover:bg-primary/30 border border-primary/30 text-primary text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {valuesLoading
                        ? <><Loader2 className="w-4 h-4 animate-spin" /> Scanning products…</>
                        : <><Search className="w-4 h-4" /> Load Values &amp; Preview</>
                      }
                    </button>

                    {valuePreviews.length > 0 && (
                      <div className="space-y-5 pt-4 border-t border-border/50">
                        {groupedPreviews.map(group => (
                          <div key={group.def.id} className="space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium text-foreground uppercase tracking-wide">
                                {group.def.name}
                                <span className="ml-2 text-muted-foreground normal-case font-normal">
                                  {group.values.length} value{group.values.length !== 1 ? 's' : ''}
                                </span>
                              </span>
                              <div className="flex gap-3">
                                <button onClick={() => setGroupInclude(group.def.id, true)} className="text-xs text-muted-foreground hover:text-primary transition-colors">Select all</button>
                                <button onClick={() => setGroupInclude(group.def.id, false)} className="text-xs text-muted-foreground hover:text-red-400 transition-colors">Deselect all</button>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {group.values.map(v => (
                                <button
                                  key={v.value}
                                  onClick={() => toggleValueInclude(v.defId, v.value)}
                                  title={`${v.count} product${v.count !== 1 ? 's' : ''}`}
                                  className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-all ${
                                    v.include
                                      ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-400'
                                      : 'bg-muted/50 border-border text-slate-600 line-through'
                                  }`}
                                >
                                  {group.prefix}{v.value}
                                  <span className="ml-1.5 opacity-50">×{v.count}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}

                        <button
                          onClick={addPreviewsToStaging}
                          disabled={includedCount === 0}
                          className="w-full py-2.5 rounded-xl bg-primary/20 hover:bg-primary/30 border border-primary/30 text-primary text-sm font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Layers className="w-4 h-4" />
                          Add {includedCount} Collection{includedCount !== 1 ? 's' : ''} to Staging
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Add Collection form */}
              <div className="glass-panel rounded-xl p-6 space-y-5">
                <h2 className="font-semibold text-foreground flex items-center gap-2">
                  <Plus className="w-4 h-4 text-primary" /> Add Collection Manually
                </h2>

                <div className="space-y-1.5">
                  <label className="text-xs text-muted-foreground uppercase tracking-wide">Name</label>
                  <input
                    className="w-full bg-muted/50 border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-slate-600 focus:outline-none focus:border-primary/50 transition-colors"
                    placeholder="e.g. Mitsubishi Triton Parts"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    onKeyDown={e => { if (e.key === 'Enter') addToStaging(); }}
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs text-muted-foreground uppercase tracking-wide">Type</label>
                  <div className="flex gap-2">
                    {(['custom', 'smart'] as const).map(t => (
                      <button
                        key={t}
                        onClick={() => setForm(f => ({ ...f, type: t }))}
                        className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border transition-all ${
                          form.type === t
                            ? 'bg-primary/15 border-primary/50 text-primary'
                            : 'bg-muted/50 border-border text-muted-foreground hover:text-foreground hover:bg-muted'
                        }`}
                      >
                        {t === 'custom' ? '📁 Custom' : '⭐ Smart'}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-slate-600">
                    {form.type === 'custom'
                      ? 'Manually curated — you add products inside Shopify.'
                      : 'Rule-based — products auto-join when they match.'}
                  </p>
                </div>

                {form.type === 'smart' && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="text-xs text-muted-foreground uppercase tracking-wide">Rules</label>
                      <Select
                        value={form.disjunctive ? 'any' : 'all'}
                        onValueChange={v => setForm(f => ({ ...f, disjunctive: v === 'any' }))}
                      >
                        <SelectTrigger className="h-7 text-xs w-36 bg-muted/50 border-border">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Match ALL rules</SelectItem>
                          <SelectItem value="any">Match ANY rule</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      {form.rules.map(rule => (
                        <div key={rule.id} className="flex gap-2 items-center">
                          <Select value={rule.column} onValueChange={v => updateRule(rule.id, { column: v })}>
                            <SelectTrigger className="h-8 text-xs flex-1 bg-muted/50 border-border min-w-0">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {COLUMN_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <Select value={rule.relation} onValueChange={v => updateRule(rule.id, { relation: v })}>
                            <SelectTrigger className="h-8 text-xs w-32 shrink-0 bg-muted/50 border-border">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {RELATION_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <input
                            className="h-8 flex-1 min-w-0 bg-muted/50 border border-border rounded-lg px-2 text-xs text-foreground placeholder:text-slate-600 focus:outline-none focus:border-primary/50 transition-colors"
                            placeholder="value"
                            value={rule.condition}
                            onChange={e => updateRule(rule.id, { condition: e.target.value })}
                          />
                          {form.rules.length > 1 && (
                            <button onClick={() => removeRule(rule.id)} className="text-slate-600 hover:text-red-400 transition-colors p-1 shrink-0">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    <button onClick={addRule} className="text-xs text-muted-foreground hover:text-primary transition-colors flex items-center gap-1">
                      <Plus className="w-3 h-3" /> Add rule
                    </button>
                  </div>
                )}

                <button
                  onClick={addToStaging}
                  className="w-full py-2.5 rounded-xl bg-primary/20 hover:bg-primary/30 border border-primary/30 text-primary text-sm font-medium transition-all flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" /> Add to List
                </button>
              </div>

              {/* Staging list */}
              <div className="glass-panel rounded-xl p-6 flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <h2 className="font-semibold text-foreground flex items-center gap-2">
                    <Layers className="w-4 h-4 text-primary" /> Pending ({staging.length})
                  </h2>
                  {staging.length > 0 && (
                    <button onClick={() => setStaging([])} className="text-xs text-slate-600 hover:text-red-400 transition-colors">
                      Clear all
                    </button>
                  )}
                </div>

                {staging.length === 0 ? (
                  <div className="flex-1 flex flex-col items-center justify-center py-14 text-center">
                    <FolderOpen className="w-10 h-10 text-slate-700 mb-3" />
                    <p className="text-muted-foreground text-sm">No collections yet</p>
                    <p className="text-slate-700 text-xs mt-1">Use the form or auto-generate from metafields above.</p>
                  </div>
                ) : (
                  <div className="flex-1 space-y-2 max-h-72 overflow-y-auto pr-1 custom-scrollbar">
                    {staging.map(col => (
                      <div key={col.id} className="bg-muted/50 rounded-lg px-3 py-2.5 flex items-start gap-3 group">
                        <span className="text-base mt-0.5 shrink-0">{col.type === 'smart' ? '⭐' : '📁'}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-foreground font-medium truncate">{col.name}</p>
                          {col.type === 'smart' && col.rules.length > 0 ? (
                            <p className="text-xs text-muted-foreground mt-0.5 truncate">
                              {col.rules.map(r => r.label ?? `${r.column} ${r.relation} "${r.condition}"`).join(col.disjunctive ? ' OR ' : ' AND ')}
                            </p>
                          ) : (
                            <p className="text-xs text-slate-600 mt-0.5">Custom collection</p>
                          )}
                        </div>
                        <button
                          onClick={() => setStaging(prev => prev.filter(c => c.id !== col.id))}
                          className="text-slate-700 hover:text-red-400 transition-colors p-0.5 opacity-0 group-hover:opacity-100 shrink-0 mt-0.5"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {staging.length > 0 && (
                  <div className="space-y-2">
                    {!selectedStoreId && (
                      <p className="text-xs text-amber-500/80 flex items-center justify-center gap-1">
                        <AlertCircle className="w-3 h-3" /> Select a store above first
                      </p>
                    )}
                    <button
                      onClick={() => createAll()}
                      disabled={creating || !selectedStoreId}
                      className="w-full py-2.5 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-400 text-sm font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {creating
                        ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating…</>
                        : <><Play className="w-4 h-4" /> Create All ({staging.length})</>
                      }
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Run History */}
            {runs.length > 0 && (
              <div className="glass-panel rounded-xl p-6 space-y-3">
                <div className="flex items-center gap-2 mb-1">
                  <History className="w-4 h-4 text-muted-foreground" />
                  <h2 className="font-semibold text-foreground">Collection Run History</h2>
                  <span className="text-xs text-muted-foreground ml-auto">{runs.length} run{runs.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="space-y-2">
                  {runs.map(run => {
                    const isOpen = expandedRunId === run.id;
                    const runResults: CollectionResult[] = Array.isArray(run.results) ? run.results : [];
                    return (
                      <div key={run.id} className="rounded-lg border border-border/50 bg-muted/30 overflow-hidden">
                        <button
                          className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/50 transition-colors"
                          onClick={() => setExpandedRunId(isOpen ? null : run.id)}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-foreground font-medium truncate">{run.storeName}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {fmtDateTime(run.createdAt)} · {run.totalCount} collections
                            </p>
                          </div>
                          <div className="flex gap-3 text-xs shrink-0">
                            {run.createdCount > 0 && <span className="text-emerald-400">{run.createdCount} created</span>}
                            {run.skippedCount > 0 && <span className="text-amber-400">{run.skippedCount} skipped</span>}
                            {run.errorCount   > 0 && <span className="text-red-400">{run.errorCount} failed</span>}
                          </div>
                          <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {isOpen && (
                          <div className="border-t border-border/50 px-4 py-3 space-y-1.5 max-h-72 overflow-y-auto">
                            {runResults.map((r, i) => (
                              <div key={i} className={`flex items-start gap-2.5 px-2.5 py-2 rounded-lg border ${
                                r.status === 'created' ? 'bg-emerald-500/10 border-emerald-500/20'
                                : r.status === 'skipped' ? 'bg-amber-500/10 border-amber-500/20'
                                : 'bg-red-500/10 border-red-500/20'
                              }`}>
                                {r.status === 'created' && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />}
                                {r.status === 'skipped' && <SkipForward   className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" />}
                                {r.status === 'error'   && <XCircle       className="w-3.5 h-3.5 text-red-400 mt-0.5 shrink-0" />}
                                <div className="min-w-0">
                                  <p className="text-xs text-foreground font-medium">{r.name}</p>
                                  {r.status === 'error' && r.error && <p className="text-xs text-red-700 dark:text-red-300 mt-0.5">{r.error}</p>}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Results */}
            {results && results.length > 0 && (
              <div className="glass-panel rounded-xl p-6 space-y-3">
                <div className="flex items-center justify-between">
                  <h2 className="font-semibold text-foreground">Results</h2>
                  <div className="flex items-center gap-3">
                    <div className="flex gap-3 text-xs text-muted-foreground">
                      <span className="text-emerald-400">{results.filter(r => r.status === 'created').length} created</span>
                      <span className="text-amber-400">{results.filter(r => r.status === 'skipped').length} skipped</span>
                      <span className="text-red-400">{results.filter(r => r.status === 'error').length} failed</span>
                    </div>
                    {results.some(r => r.status === 'error') && (
                      <button
                        onClick={retryFailed}
                        disabled={creating}
                        className="flex items-center gap-1.5 text-xs text-amber-400 hover:text-amber-300 border border-amber-500/20 hover:border-amber-400/40 rounded-lg px-2.5 py-1.5 transition-all disabled:opacity-50 disabled:pointer-events-none"
                      >
                        {creating
                          ? <><Loader2 className="w-3 h-3 animate-spin" /> Retrying…</>
                          : <><RefreshCw className="w-3 h-3" /> Retry {results.filter(r => r.status === 'error').length} failed</>
                        }
                      </button>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  {results.map((r, i) => (
                    <div key={i} className={`flex items-start gap-3 px-3 py-2.5 rounded-lg border ${
                      r.status === 'created' ? 'bg-emerald-500/10 border-emerald-500/20'
                      : r.status === 'skipped' ? 'bg-amber-500/10 border-amber-500/20'
                      : 'bg-red-500/10 border-red-500/20'
                    }`}>
                      {r.status === 'created' && <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />}
                      {r.status === 'skipped' && <SkipForward className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />}
                      {r.status === 'error'   && <XCircle     className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />}
                      <div className="min-w-0">
                        <p className="text-sm text-foreground font-medium">{r.name}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {r.status === 'created' && 'Created successfully in Shopify'}
                          {r.status === 'skipped' && 'Already exists — skipped'}
                          {r.status === 'error'   && (r.error ?? 'Unknown error')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* ════════════════════════════════════════════════════════════════════ */}
        {/* TAB: Metafield Linker                                               */}
        {/* ════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'linker' && (
          <div className="space-y-5">
            {!selectedStoreId ? (
              <div className="glass-panel rounded-xl p-8 text-center text-muted-foreground">
                <Link2 className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p>Select a store above to get started</p>
              </div>
            ) : (
              <>
                {/* Explainer */}
                <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-primary/5 border border-primary/20 text-xs text-primary/80">
                  <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>
                    Select a parent collection (e.g. <strong>Toyota</strong>), pick the metafield you want to populate
                    (e.g. <code>custom.sub_models</code>), then either manually choose sub-collections or use
                    <strong> Auto-link</strong> to scan products and auto-match values to collections.
                  </span>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  {/* Step 1 + 2: Parent collection + metafield */}
                  <div className="glass-panel rounded-xl p-6 space-y-5">
                    {/* Step 1: Parent collection */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="w-5 h-5 rounded-full bg-primary/20 border border-primary/40 text-primary text-xs flex items-center justify-center font-bold shrink-0">1</span>
                        <h3 className="text-sm font-semibold text-foreground">Parent collection</h3>
                      </div>
                      <CollectionSearch
                        storeId={selectedStoreId}
                        token={token ?? ''}
                        value={parentCollection}
                        onChange={c => {
                          setParentCollection(c);
                          setSelectedColMeta(null);
                          setProposals([]);
                          setManualRefs([]);
                          setApplyResult(null);
                        }}
                        placeholder="Search collections…"
                      />
                    </div>

                    {/* Step 2: Target metafield */}
                    {parentCollection && (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="w-5 h-5 rounded-full bg-primary/20 border border-primary/40 text-primary text-xs flex items-center justify-center font-bold shrink-0">2</span>
                          <h3 className="text-sm font-semibold text-foreground">Target metafield</h3>
                        </div>

                        {colMetaDefsLoading ? (
                          <div className="flex items-center gap-2 text-muted-foreground text-xs py-2">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading collection metafields…
                          </div>
                        ) : colMetaDefs.length === 0 ? (
                          <p className="text-xs text-muted-foreground py-2">
                            No collection metafield definitions found.
                            Create them in Shopify under <strong>Settings → Custom data → Collections</strong>.
                          </p>
                        ) : (
                          <div className="space-y-1.5">
                            {colMetaDefs.map(def => (
                              <button
                                key={def.id}
                                onClick={() => {
                                  setSelectedColMeta(def);
                                  setProposals([]);
                                  setManualRefs([]);
                                  setApplyResult(null);
                                }}
                                className={`w-full text-left px-3 py-2.5 rounded-lg border transition-all ${
                                  selectedColMeta?.id === def.id
                                    ? 'bg-primary/15 border-primary/40 text-primary'
                                    : 'bg-muted/50 border-border/50 hover:border-border text-foreground hover:text-foreground'
                                }`}
                              >
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm font-medium">{def.name}</span>
                                  <span className="text-xs opacity-50">{def.namespace}.{def.key}</span>
                                  <span className={`text-xs px-1.5 py-0.5 rounded font-mono border ${
                                    isCollectionRefType(def.type.name)
                                      ? 'bg-violet-500/15 border-violet-500/30 text-violet-400'
                                      : 'bg-muted/50 border-border text-muted-foreground'
                                  }`}>
                                    {def.type.name}
                                  </span>
                                </div>
                                {def.description && (
                                  <p className="text-xs text-slate-600 mt-0.5 truncate">{def.description}</p>
                                )}
                              </button>
                            ))}
                          </div>
                        )}

                        {currentMetaValue && selectedColMeta && (
                          <div className="mt-1 px-3 py-2 rounded-lg bg-muted/30 border border-border/50 text-xs text-muted-foreground">
                            <span className="text-muted-foreground font-medium">Current value: </span>
                            <span className="font-mono break-all">{currentMetaValue.length > 120 ? currentMetaValue.slice(0, 120) + '…' : currentMetaValue}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Step 3: Set value */}
                  {parentCollection && selectedColMeta && (
                    <div className="glass-panel rounded-xl p-6 space-y-5">
                      <div className="flex items-center gap-2">
                        <span className="w-5 h-5 rounded-full bg-primary/20 border border-primary/40 text-primary text-xs flex items-center justify-center font-bold shrink-0">3</span>
                        <h3 className="text-sm font-semibold text-foreground">
                          Set value
                          <span className="ml-2 text-xs font-normal text-muted-foreground">
                            → <em>{selectedColMeta.name}</em> on <em>{parentCollection.title}</em>
                          </span>
                        </h3>
                      </div>

                      {isCollectionRefType(selectedColMeta.type.name) ? (
                        <>
                          {/* Mode toggle */}
                          <div className="flex gap-1 p-1 bg-muted/50 rounded-lg w-fit">
                            {([
                              { id: 'manual', label: 'Manual pick' },
                              { id: 'auto',   label: 'Auto-link'  },
                            ] as const).map(m => (
                              <button
                                key={m.id}
                                onClick={() => { setLinkerMode(m.id); setProposals([]); setApplyResult(null); }}
                                className={`px-4 py-1.5 rounded-md text-xs font-medium transition-all ${
                                  linkerMode === m.id
                                    ? 'bg-primary/20 border border-primary/30 text-primary'
                                    : 'text-muted-foreground hover:text-foreground'
                                }`}
                              >
                                {m.label}
                              </button>
                            ))}
                          </div>

                          {/* ── Manual mode ─────────────────────────────── */}
                          {linkerMode === 'manual' && (
                            <div className="space-y-3">
                              <p className="text-xs text-muted-foreground">
                                {selectedColMeta.type.name === 'list.collection_reference'
                                  ? 'Pick one or more collections to store as references.'
                                  : 'Pick a single collection to store as a reference.'}
                              </p>

                              <CollectionSearch
                                storeId={selectedStoreId}
                                token={token ?? ''}
                                value={null}
                                onChange={c => { if (c) addManualRef(c); }}
                                placeholder="Add a collection…"
                              />

                              {manualRefs.length > 0 && (
                                <div className="space-y-1.5">
                                  {manualRefs.map(ref => (
                                    <div key={ref.id} className="flex items-center gap-2 px-3 py-2 bg-muted/50 border border-border/50 rounded-lg group">
                                      <Layers className="w-3 h-3 text-muted-foreground shrink-0" />
                                      <span className="flex-1 text-sm text-foreground truncate">{ref.title}</span>
                                      <button
                                        onClick={() => removeManualRef(ref.id)}
                                        className="text-slate-600 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100 shrink-0"
                                      >
                                        <X className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {selectedColMeta.type.name === 'collection_reference' && manualRefs.length > 1 && (
                                <p className="text-xs text-amber-500/80 flex items-center gap-1">
                                  <AlertCircle className="w-3 h-3" /> This is a single reference — only the first will be saved.
                                </p>
                              )}
                            </div>
                          )}

                          {/* ── Auto-link mode ───────────────────────────── */}
                          {linkerMode === 'auto' && (
                            <div className="space-y-4">
                              <div className="space-y-2">
                                <p className="text-xs text-muted-foreground">
                                  Pick a product metafield to scan. We'll aggregate all distinct values across products in
                                  <strong> {parentCollection.title}</strong>, then find matching sub-collections by title.
                                </p>

                                {productMetaDefsLoading ? (
                                  <div className="flex items-center gap-2 text-muted-foreground text-xs py-2">
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading product metafields…
                                  </div>
                                ) : productMetaDefs.length === 0 ? (
                                  <p className="text-xs text-muted-foreground">No product metafield definitions found.</p>
                                ) : (
                                  <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1 custom-scrollbar">
                                    {productMetaDefs.map(def => (
                                      <button
                                        key={def.id}
                                        onClick={() => { setSelectedProductMeta(def); setProposals([]); setApplyResult(null); }}
                                        className={`w-full text-left px-3 py-2 rounded-lg border transition-all text-xs ${
                                          selectedProductMeta?.id === def.id
                                            ? 'bg-primary/15 border-primary/40 text-primary'
                                            : 'bg-muted/50 border-border/50 hover:border-border text-muted-foreground hover:text-foreground'
                                        }`}
                                      >
                                        <span className="font-medium">{def.name}</span>
                                        <span className="ml-2 opacity-50">{def.namespace}.{def.key}</span>
                                        <span className="ml-2 opacity-40 font-mono">{def.type.name}</span>
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>

                              <button
                                onClick={runAutoLink}
                                disabled={!selectedProductMeta || scanning}
                                className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary/20 hover:bg-primary/30 border border-primary/30 text-primary text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {scanning
                                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Scanning products…</>
                                  : <><Search className="w-4 h-4" /> Scan &amp; Match</>
                                }
                              </button>

                              {/* Proposals */}
                              {proposals.length > 0 && (
                                <div className="space-y-3 pt-2 border-t border-border/50">
                                  <div className="flex items-center justify-between">
                                    <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
                                      {proposals.length} value{proposals.length !== 1 ? 's' : ''} found
                                      {scanPages > 0 && <span className="ml-1.5 normal-case font-normal text-slate-600">({scanPages} page{scanPages !== 1 ? 's' : ''} scanned)</span>}
                                    </p>
                                    <div className="flex gap-3 text-xs">
                                      <span className="text-emerald-400">{confirmedProposals.length} matched</span>
                                      {unmatchedProposals.length > 0 && <span className="text-amber-400">{unmatchedProposals.length} unmatched</span>}
                                    </div>
                                  </div>

                                  <div className="space-y-2 max-h-80 overflow-y-auto pr-1 custom-scrollbar">
                                    {proposals.map((p, idx) => {
                                      const resolved = p.override !== undefined ? p.override : p.matchedCollection;
                                      return (
                                        <div
                                          key={p.value}
                                          className={`rounded-lg border px-3 py-2.5 transition-all ${
                                            p.excluded
                                              ? 'bg-muted/30 border-border/50 opacity-50'
                                              : resolved
                                                ? 'bg-emerald-500/8 border-emerald-500/20'
                                                : 'bg-amber-500/8 border-amber-500/20'
                                          }`}
                                        >
                                          <div className="flex items-center gap-2 flex-wrap">
                                            {/* Value badge */}
                                            <span className="text-xs font-mono bg-muted/60 border border-border px-2 py-0.5 rounded text-foreground shrink-0">
                                              {p.value}
                                            </span>

                                            {!p.excluded && (
                                              <>
                                                <ArrowRight className="w-3 h-3 text-slate-600 shrink-0" />

                                                {overridingIdx === idx ? (
                                                  <div className="flex-1 min-w-0">
                                                    <CollectionSearch
                                                      storeId={selectedStoreId}
                                                      token={token ?? ''}
                                                      value={null}
                                                      onChange={c => {
                                                        setProposals(prev => prev.map((pr, i) =>
                                                          i === idx ? { ...pr, override: c } : pr
                                                        ));
                                                        setOverridingIdx(null);
                                                      }}
                                                      placeholder="Search collections…"
                                                    />
                                                  </div>
                                                ) : resolved ? (
                                                  <div className="flex items-center gap-1.5 flex-1 min-w-0">
                                                    <CheckCircle2 className="w-3 h-3 text-emerald-400 shrink-0" />
                                                    <span className="text-sm text-foreground truncate">{resolved.title}</span>
                                                    <button
                                                      onClick={() => setOverridingIdx(idx)}
                                                      className="text-slate-600 hover:text-primary transition-colors shrink-0 ml-1"
                                                      title="Change match"
                                                    >
                                                      <Pencil className="w-3 h-3" />
                                                    </button>
                                                  </div>
                                                ) : (
                                                  <div className="flex items-center gap-1.5 flex-1 min-w-0">
                                                    <AlertCircle className="w-3 h-3 text-amber-400 shrink-0" />
                                                    <span className="text-xs text-amber-400/80">No match</span>
                                                    {p.candidates.length > 0 && (
                                                      <div className="flex gap-1 flex-wrap ml-1">
                                                        {p.candidates.slice(0, 3).map(c => (
                                                          <button
                                                            key={c.id}
                                                            onClick={() => setProposals(prev => prev.map((pr, i) =>
                                                              i === idx ? { ...pr, override: c } : pr
                                                            ))}
                                                            className="text-xs text-muted-foreground hover:text-foreground px-1.5 py-0.5 rounded bg-muted/50 border border-border hover:border-border transition-all"
                                                          >
                                                            {c.title}
                                                          </button>
                                                        ))}
                                                      </div>
                                                    )}
                                                    <button
                                                      onClick={() => setOverridingIdx(idx)}
                                                      className="text-xs text-primary hover:text-primary/80 transition-colors shrink-0"
                                                    >
                                                      Search…
                                                    </button>
                                                  </div>
                                                )}
                                              </>
                                            )}

                                            {/* Exclude/include toggle */}
                                            <button
                                              onClick={() => {
                                                setProposals(prev => prev.map((pr, i) =>
                                                  i === idx ? { ...pr, excluded: !pr.excluded } : pr
                                                ));
                                                if (overridingIdx === idx) setOverridingIdx(null);
                                              }}
                                              className="ml-auto text-slate-600 hover:text-red-400 transition-colors shrink-0"
                                              title={p.excluded ? 'Re-include' : 'Exclude'}
                                            >
                                              {p.excluded
                                                ? <Plus className="w-3.5 h-3.5 text-muted-foreground hover:text-primary" />
                                                : <X className="w-3.5 h-3.5" />
                                              }
                                            </button>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      ) : (
                        /* Non-reference metafield: plain text input */
                        <div className="space-y-2">
                          <label className="text-xs text-muted-foreground uppercase tracking-wide">Value</label>
                          <input
                            className="w-full bg-muted/50 border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-slate-600 focus:outline-none focus:border-primary/50 transition-colors"
                            placeholder={`Enter ${selectedColMeta.type.name} value…`}
                            value={manualTextValue}
                            onChange={e => setManualTextValue(e.target.value)}
                          />
                          <p className="text-xs text-slate-600">Type: <span className="font-mono">{selectedColMeta.type.name}</span></p>
                        </div>
                      )}

                      {/* Apply button */}
                      <div className="pt-2 border-t border-border/50 space-y-3">
                        {applyResult && (
                          <div className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm ${
                            applyResult.ok
                              ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
                              : 'bg-red-500/10 border-red-500/20 text-red-400'
                          }`}>
                            {applyResult.ok
                              ? <CheckCircle2 className="w-4 h-4 shrink-0" />
                              : <XCircle className="w-4 h-4 shrink-0" />
                            }
                            {applyResult.message}
                          </div>
                        )}

                        <button
                          onClick={applyLinker}
                          disabled={applying}
                          className="w-full py-2.5 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-400 text-sm font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {applying
                            ? <><Loader2 className="w-4 h-4 animate-spin" /> Applying…</>
                            : <><Check className="w-4 h-4" /> Apply to Shopify</>
                          }
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════ */}
        {/* AI Description Writer tab                                           */}
        {/* ════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'ai-writer' && (
          <div className="space-y-6">

            {/* Step 1 — Select Collections */}
            <div className="bg-muted/30 border border-border/60 rounded-2xl p-6 space-y-4">
              <div className="flex items-center gap-3 mb-1">
                <div className="w-6 h-6 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center text-primary text-xs font-bold shrink-0">1</div>
                <h3 className="text-sm font-semibold text-foreground">Select Collections</h3>
              </div>

              {/* Mode toggle */}
              <div className="flex gap-1 p-1 bg-muted/50 border border-border/50 rounded-xl w-fit">
                {([
                  { id: 'manual',    label: 'Manual Search' },
                  { id: 'metafield', label: 'Filter by Metafield' },
                ] as const).map(m => (
                  <button
                    key={m.id}
                    onClick={() => {
                      aiSearchRequestRef.current++;
                      aiSearchAbortRef.current?.abort();
                      setAiSelectMode(m.id); setAiSelectedCollections([]); setAiSearchQuery('');
                      setAiSearchResults([]); setAiSearchError(null);
                      setAiSearchPageInfo({ hasNextPage: false, endCursor: null });
                    }}
                    className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      aiSelectMode === m.id
                        ? 'bg-primary/20 border border-primary/30 text-primary'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>

              {aiSelectMode === 'manual' ? (
                <div className="space-y-3">
                  <div ref={aiSearchRef} className="relative">
                    <div className="flex items-center gap-2 px-3 py-2 bg-muted/50 border border-border rounded-lg focus-within:border-primary/50 transition-colors">
                      <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                      <input
                        className="flex-1 bg-transparent text-sm text-foreground placeholder:text-slate-600 outline-none"
                        placeholder="Search collections to add…"
                        value={aiSearchQuery}
                        onChange={e => { setAiSearchQuery(e.target.value); setAiSearchOpen(true); }}
                        onFocus={() => setAiSearchOpen(true)}
                        disabled={!selectedStoreId}
                      />
                      {aiSearchLoading && <Loader2 className="w-3.5 h-3.5 text-muted-foreground animate-spin shrink-0" />}
                    </div>
                    {aiSearchOpen && aiSearchResults.length > 0 && (
                      <div className="absolute z-50 top-full mt-1 left-0 right-0 bg-card border border-border rounded-lg shadow-xl max-h-60 overflow-y-auto">
                        {aiSearchResults.map(c => (
                          <button
                            key={c.id}
                            onClick={() => addAiCollection(c)}
                            className="w-full text-left px-3 py-2.5 hover:bg-muted/50 transition-colors flex items-center gap-2"
                          >
                            <Layers className="w-3 h-3 text-muted-foreground shrink-0" />
                            <span className="text-sm text-foreground flex-1 truncate">{c.title}</span>
                            <span className="text-xs text-slate-600 shrink-0">{c.handle}</span>
                          </button>
                        ))}
                          {aiSearchPageInfo.hasNextPage && (
                            <button
                              type="button"
                              onClick={() => void fetchAiCollections(aiSearchPageInfo.endCursor, true)}
                              disabled={aiSearchLoading}
                              className="w-full px-3 py-2.5 text-xs text-primary hover:bg-muted/50 disabled:opacity-50"
                            >
                              {aiSearchLoading ? 'Loading…' : 'Load more collections'}
                            </button>
                          )}
                      </div>
                    )}
                      {aiSearchOpen && !aiSearchLoading && aiSearchError && (
                        <div className="absolute z-50 top-full mt-1 left-0 right-0 bg-card border border-red-500/40 rounded-lg shadow-xl px-3 py-2.5">
                          <p className="text-xs text-red-400">{aiSearchError}</p>
                        </div>
                      )}
                     {aiSearchOpen && !aiSearchLoading && !aiSearchError && aiSearchResults.length === 0 && aiSearchQuery.length > 1 && (
                      <div className="absolute z-50 top-full mt-1 left-0 right-0 bg-card border border-border rounded-lg shadow-xl px-3 py-2.5">
                        <p className="text-xs text-muted-foreground">No collections found</p>
                      </div>
                    )}
                  </div>
                  {!selectedStoreId && <p className="text-xs text-muted-foreground">Select a store above to search collections.</p>}
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-xs text-muted-foreground">Load all collections that have a non-empty value for a specific metafield.</p>
                  <div className="flex gap-2">
                    <div className="flex-1 space-y-1">
                      <label className="text-xs text-muted-foreground uppercase tracking-wide">Namespace</label>
                      <input
                        className="w-full bg-muted/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-slate-600 focus:outline-none focus:border-primary/50 transition-colors"
                        placeholder="e.g. custom"
                        value={aiMetaNs}
                        onChange={e => setAiMetaNs(e.target.value)}
                      />
                    </div>
                    <div className="flex-1 space-y-1">
                      <label className="text-xs text-muted-foreground uppercase tracking-wide">Key</label>
                      <input
                        className="w-full bg-muted/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-slate-600 focus:outline-none focus:border-primary/50 transition-colors"
                        placeholder="e.g. category"
                        value={aiMetaKey}
                        onChange={e => setAiMetaKey(e.target.value)}
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        onClick={loadByMetafield}
                        disabled={aiMetaLoading || !selectedStoreId}
                        className="px-4 py-2 bg-primary/20 hover:bg-primary/30 border border-primary/30 text-primary rounded-lg text-sm font-medium transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {aiMetaLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                        Load
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Selected collection chips */}
              {aiSelectedCollections.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-muted-foreground">{aiSelectedCollections.length} collection{aiSelectedCollections.length !== 1 ? 's' : ''} selected</p>
                    <button onClick={() => setAiSelectedCollections([])} className="text-xs text-slate-600 hover:text-red-400 transition-colors">Clear all</button>
                  </div>
                  <div className="flex flex-wrap gap-2 max-h-36 overflow-y-auto">
                    {aiSelectedCollections.map(c => (
                      <div key={c.id} className="flex items-center gap-1.5 px-2.5 py-1 bg-primary/10 border border-primary/20 rounded-full">
                        <Layers className="w-3 h-3 text-primary shrink-0" />
                        <span className="text-xs text-foreground">{c.title}</span>
                        <button onClick={() => removeAiCollection(c.id)} className="text-muted-foreground hover:text-red-400 transition-colors">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Generate button */}
            <button
              onClick={generateDescriptions}
              disabled={aiGenerating || aiSelectedCollections.length === 0 || !selectedStoreId}
              className="w-full py-3 rounded-xl bg-primary/20 hover:bg-primary/30 border border-primary/30 text-primary font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {aiGenerating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {aiPhase === 'fetching'
                    ? `Fetching products… (${aiProgress.current}/${aiProgress.total})`
                    : `Generating descriptions… (${aiProgress.current}/${aiProgress.total})`
                  }
                </>
              ) : (
                <>
                  <Wand2 className="w-4 h-4" />
                  Generate Descriptions{aiSelectedCollections.length > 0 ? ` (${aiSelectedCollections.length} collection${aiSelectedCollections.length !== 1 ? 's' : ''})` : ''}
                </>
              )}
            </button>

            {/* Step 2 — Review & Edit */}
            {aiResults.length > 0 && (
              <div className="bg-muted/30 border border-border/60 rounded-2xl p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400 text-xs font-bold shrink-0">2</div>
                    <h3 className="text-sm font-semibold text-foreground">Review &amp; Edit</h3>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setAiResults(prev => prev.map(r => ({ ...r, approved: true })))}
                      className="text-xs px-3 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/20 transition-colors"
                    >
                      Approve all
                    </button>
                    <button
                      onClick={() => setAiResults(prev => prev.map(r => ({ ...r, approved: false })))}
                      className="text-xs px-3 py-1 rounded-lg bg-muted/50 border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                    >
                      Deselect all
                    </button>
                  </div>
                </div>

                <div className="space-y-4">
                  {aiResults.map((result, i) => (
                    <div key={result.collectionId} className={`rounded-xl border p-4 space-y-3 transition-colors ${
                      result.approved ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-border/60 bg-muted/30'
                    }`}>
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={result.approved}
                          onChange={e => setAiResults(prev => prev.map((r, j) => j === i ? { ...r, approved: e.target.checked } : r))}
                          className="mt-0.5 accent-primary shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{result.title}</p>
                          <p className="text-xs text-muted-foreground">{result.handle}</p>
                        </div>
                        {result.approved && <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />}
                      </div>

                      {result.currentDescription && (
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground uppercase tracking-wide">Current description</p>
                          <div
                            className="text-xs text-muted-foreground bg-muted/30 rounded-lg px-3 py-2 max-h-20 overflow-y-auto leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(result.currentDescription) }}
                          />
                        </div>
                      )}

                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground uppercase tracking-wide">AI-generated description</p>
                        <textarea
                          rows={5}
                          className="w-full bg-muted/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-slate-600 focus:outline-none focus:border-primary/50 transition-colors resize-y leading-relaxed"
                          value={result.editedDescription}
                          onChange={e => setAiResults(prev => prev.map((r, j) => j === i ? { ...r, editedDescription: e.target.value } : r))}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Apply section */}
                <div className="pt-2 border-t border-border/50 space-y-3">
                  {aiApplyResult && (
                    <div className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm ${
                      aiApplyResult.ok
                        ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
                        : 'bg-red-500/10 border-red-500/20 text-red-400'
                    }`}>
                      {aiApplyResult.ok
                        ? <CheckCircle2 className="w-4 h-4 shrink-0" />
                        : <XCircle className="w-4 h-4 shrink-0" />
                      }
                      {aiApplyResult.message}
                    </div>
                  )}
                  <button
                    onClick={applyDescriptions}
                    disabled={aiApplying || aiResults.filter(r => r.approved).length === 0}
                    className="w-full py-2.5 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-400 text-sm font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {aiApplying
                      ? <><Loader2 className="w-4 h-4 animate-spin" /> Applying…</>
                      : <><Check className="w-4 h-4" /> Apply {aiResults.filter(r => r.approved).length} Description{aiResults.filter(r => r.approved).length !== 1 ? 's' : ''} to Shopify</>
                    }
                  </button>
                </div>
              </div>
            )}

          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════ */}
        {/* TAB: OOS to End                                                     */}
        {/* ════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'oos-sort' && (
          <div className="space-y-6">

            {/* Info banner */}
            <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-blue-500/5 border border-blue-500/20 text-xs text-blue-700 dark:text-blue-300/80">
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                Reorders products in each collection so that out-of-stock items (all variants ≤ 0 inventory) appear at the end,
                while in-stock products keep their relative order. Collections must be on <strong>Manual</strong> sort — they will be switched automatically if needed.
                Smart collections are skipped.
              </span>
            </div>

            {/* Step 1 — Pick collections */}
            <div className="bg-muted/30 border border-border/60 rounded-2xl p-6 space-y-4">
              <div className="flex items-center gap-3 mb-1">
                <div className="w-6 h-6 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center text-primary text-xs font-bold shrink-0">1</div>
                <h3 className="text-sm font-semibold text-foreground">Select Collections</h3>
              </div>

              {/* Mode toggle */}
              <div className="flex gap-1 p-1 bg-muted/50 border border-border/50 rounded-xl w-fit">
                {([
                  { id: 'manual', label: 'Pick collections' },
                  { id: 'all',    label: 'All collections' },
                ] as const).map(m => (
                  <button
                    key={m.id}
                    onClick={() => { setOosMode(m.id); setOosSelectedCollections([]); setOosResults([]); }}
                    className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      oosMode === m.id
                        ? 'bg-primary/20 border border-primary/30 text-primary'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>

              {oosMode === 'manual' ? (
                <div className="space-y-3">
                  <div ref={oosSearchRef} className="relative">
                    <div className="flex items-center gap-2 px-3 py-2 bg-muted/50 border border-border rounded-lg focus-within:border-primary/50 transition-colors">
                      <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                      <input
                        className="flex-1 bg-transparent text-sm text-foreground placeholder:text-slate-600 outline-none"
                        placeholder="Search collections to add…"
                        value={oosSearchQuery}
                        onChange={e => { setOosSearchQuery(e.target.value); setOosSearchOpen(true); }}
                        onFocus={() => setOosSearchOpen(true)}
                        disabled={!selectedStoreId}
                      />
                      {oosSearchLoading && <Loader2 className="w-3.5 h-3.5 text-muted-foreground animate-spin shrink-0" />}
                    </div>
                    {oosSearchOpen && oosSearchResults.length > 0 && (
                      <div className="absolute z-50 top-full mt-1 left-0 right-0 bg-card border border-border rounded-lg shadow-xl max-h-60 overflow-y-auto">
                        {oosSearchResults.map(c => (
                          <button
                            key={c.id}
                            onClick={() => addOosCollection(c)}
                            className="w-full text-left px-3 py-2.5 hover:bg-muted/50 transition-colors flex items-center gap-2"
                          >
                            <Layers className="w-3 h-3 text-muted-foreground shrink-0" />
                            <span className="text-sm text-foreground flex-1 truncate">{c.title}</span>
                            <span className="text-xs text-slate-600 shrink-0">{c.handle}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {oosSearchOpen && !oosSearchLoading && oosSearchResults.length === 0 && oosSearchQuery.length > 1 && (
                      <div className="absolute z-50 top-full mt-1 left-0 right-0 bg-card border border-border rounded-lg shadow-xl px-3 py-2.5">
                        <p className="text-xs text-muted-foreground">No collections found</p>
                      </div>
                    )}
                  </div>
                  {!selectedStoreId && <p className="text-xs text-muted-foreground">Select a store above to search collections.</p>}

                  {/* Selected chips */}
                  {oosSelectedCollections.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-muted-foreground">{oosSelectedCollections.length} collection{oosSelectedCollections.length !== 1 ? 's' : ''} selected</p>
                        <button onClick={() => setOosSelectedCollections([])} className="text-xs text-slate-600 hover:text-red-400 transition-colors">Clear all</button>
                      </div>
                      <div className="flex flex-wrap gap-2 max-h-36 overflow-y-auto">
                        {oosSelectedCollections.map(c => (
                          <div key={c.id} className="flex items-center gap-1.5 px-2.5 py-1 bg-primary/10 border border-primary/20 rounded-full">
                            <Layers className="w-3 h-3 text-primary shrink-0" />
                            <span className="text-xs text-foreground">{c.title}</span>
                            <button
                              onClick={() => setOosSelectedCollections(prev => prev.filter(x => x.id !== c.id))}
                              className="text-muted-foreground hover:text-red-400 transition-colors"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Will process every custom collection in your store. Smart collections are automatically skipped.
                </p>
              )}
            </div>

            {/* Run button */}
            <button
              onClick={runOosSort}
              disabled={oosRunning || !selectedStoreId || (oosMode === 'manual' && oosSelectedCollections.length === 0)}
              className="w-full py-3 rounded-xl bg-primary/20 hover:bg-primary/30 border border-primary/30 text-primary font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {oosRunning ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {oosStatusMsg || (oosProgress.total > 0
                    ? `Sorting… (${oosProgress.current}/${oosProgress.total})`
                    : 'Starting…'
                  )}
                </>
              ) : (
                <>
                  <ChevronsDown className="w-4 h-4" />
                  Push Out-of-Stock to End
                  {oosMode === 'manual' && oosSelectedCollections.length > 0
                    ? ` (${oosSelectedCollections.length} collection${oosSelectedCollections.length !== 1 ? 's' : ''})`
                    : oosMode === 'all' ? ' (all collections)' : ''
                  }
                </>
              )}
            </button>

            {/* Progress bar */}
            {oosRunning && oosProgress.total > 0 && (
              <div className="space-y-1.5">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{oosProgress.current} of {oosProgress.total} done</span>
                  <span>{Math.round((oosProgress.current / oosProgress.total) * 100)}%</span>
                </div>
                <div className="h-1.5 bg-muted/50 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary/60 rounded-full transition-all duration-300"
                    style={{ width: `${(oosProgress.current / oosProgress.total) * 100}%` }}
                  />
                </div>
              </div>
            )}

            {/* Results */}
            {oosResults.length > 0 && (
              <div className="bg-muted/30 border border-border/60 rounded-2xl p-6 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-foreground">Results</h3>
                  <div className="flex gap-3 text-xs text-muted-foreground">
                    <span className="text-emerald-400">{oosResults.filter(r => !r.skipped && !r.error && r.movedCount > 0).length} updated</span>
                    {oosResults.filter(r => r.movedCount === 0 && !r.skipped && !r.error).length > 0 && (
                      <span>{oosResults.filter(r => r.movedCount === 0 && !r.skipped && !r.error).length} unchanged</span>
                    )}
                    {oosResults.filter(r => r.skipped).length > 0 && (
                      <span className="text-amber-400">{oosResults.filter(r => r.skipped).length} skipped</span>
                    )}
                    {oosResults.filter(r => r.error).length > 0 && (
                      <span className="text-red-400">{oosResults.filter(r => r.error).length} errors</span>
                    )}
                  </div>
                </div>

                <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                  {oosResults.map(r => (
                    <div
                      key={r.collectionId}
                      className={`flex items-start gap-3 px-3 py-2.5 rounded-lg border text-xs ${
                        r.error
                          ? 'bg-red-500/5 border-red-500/20'
                          : r.skipped
                          ? 'bg-amber-500/5 border-amber-500/20'
                          : r.movedCount > 0
                          ? 'bg-emerald-500/5 border-emerald-500/20'
                          : 'bg-muted/30 border-border/60'
                      }`}
                    >
                      {r.error ? (
                        <XCircle className="w-3.5 h-3.5 text-red-400 mt-0.5 shrink-0" />
                      ) : r.skipped ? (
                        <SkipForward className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" />
                      ) : r.movedCount > 0 ? (
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />
                      ) : (
                        <Check className="w-3.5 h-3.5 text-muted-foreground mt-0.5 shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-foreground truncate">{r.title}</p>
                        {r.error && <p className="text-red-400 mt-0.5">{r.error}</p>}
                        {r.skipped && <p className="text-amber-400/80 mt-0.5">{r.reason}</p>}
                        {!r.error && !r.skipped && (
                          <p className="text-muted-foreground mt-0.5">
                            {r.movedCount > 0
                              ? `${r.movedCount} out-of-stock product${r.movedCount !== 1 ? 's' : ''} moved to end of ${r.totalCount} total`
                              : r.message || 'No out-of-stock products — nothing to reorder'
                            }
                            {r.switchedToManual && <span className="text-muted-foreground"> · switched to manual sort</span>}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>
        )}

      </div>
    </Layout>
  );
}
